// ─── src/components/cards/editorial/FeatureCard.jsx ──────────────────────────
// Split-screen editorial feature card.
// 60% image + 40% coloured content panel (or reversed).
// Inspired by SheerLuxe's "Key New-Season Pieces" blush panel card.
//
// variants:
//   "image-left"  (default) → image left, text panel right
//   "image-right"           → text panel left, image right
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import { GD, NU, T, S, RATIO, GOLD, resolvePalette, MetaRow, BookmarkIcon } from './cardTokens';
import { useBreakpoint } from '../../../hooks/useWindowWidth';
import { imgUrl } from '../../../utils/imgUrl';

// Perceived luminance (0-255) of a #rgb / #rrggbb colour; ~128 splits dark/light.
function bgLuminance(hex) {
  if (!hex || typeof hex !== 'string') return 255;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return 255;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

export default function FeatureCard({ data = {} }) {
  const {
    image, title, excerpt, category, date,
    bullets,             // optional ✓ checklist (e.g. photographer "Perfect For")
    onClick, saved = false, onSave,
    accentBg,
    variant = 'image-left',
    theme = 'auto',
    minHeight,            // optional per-section override (e.g. a full-body portrait)
    imageFocus,           // optional object-position for the image (e.g. 'center 30%')
    imageAspect,          // optional CSS aspect-ratio for portrait images (e.g. '2/3')
  } = data;
  const cardMin = minHeight || 700;

  const ctx   = useTheme();
  const C     = resolvePalette(ctx, theme);
  const [hover, setHover] = useState(false);
  const { isMobile } = useBreakpoint();

  // Determine colours from the panel bg's actual luminance, not a hex-prefix
  // guess. The old prefix check only treated #d/#e/#f as light, so a light
  // sage like #CFDBCB (starts #c) fell through to white text on a pale panel.
  // Default to C.dark for the panel (works in both modes).
  const panelBg   = accentBg || C.dark;
  const isLightBg = accentBg ? bgLuminance(accentBg) >= 128 : false;
  const textColor = isLightBg ? '#1a1a1a' : '#ffffff';
  const metaColor = isLightBg ? 'rgba(40,40,40,0.6)' : 'rgba(255,255,255,0.55)';

  const imageCol = (
    <div style={{
      flex: isMobile ? '0 0 100%' : imageAspect ? '0 0 50%' : '0 0 60%',
      position: 'relative',
      overflow: 'hidden',
      minHeight: isMobile ? (minHeight ? 600 : 420) : cardMin,
    }}>
      {image && (
        <img
          src={imgUrl(image, { width: 1600 })} alt={title || ''} loading="lazy"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: imageFocus || 'center',
            transform: hover ? 'scale(1.03)' : 'scale(1)',
            transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        />
      )}
    </div>
  );

  const textCol = (
    <div style={{
      flex: isMobile ? '0 0 100%' : imageAspect ? '1 1 0%' : '0 0 40%',
      background: panelBg,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: isMobile ? `${S.xxl}px ${S.lg}px` : `clamp(56px, 7vw, 96px) clamp(48px, 5vw, 80px)`,
      position: 'relative',
    }}>
      <MetaRow
        category={category}
        date={date}
        color={metaColor}
        style={{ marginBottom: S.md }}
      />

      <h2 style={{
        ...T.titleLg,
        color: textColor,
        margin: `0 0 ${S.md}px`,
        fontSize: 'clamp(28px, 3.5vw, 52px)',
      }}>
        {title}
      </h2>

      {excerpt && excerpt.split('\n\n').filter(Boolean).map((para, i, arr) => (
        <p key={i} style={{
          ...T.body,
          color: isLightBg ? 'rgba(30,30,30,0.75)' : 'rgba(255,255,255,0.65)',
          fontSize: 16, lineHeight: 1.9,
          margin: i < arr.length - 1 ? `0 0 ${S.lg}px` : `0 0 ${S.xl}px`,
        }}>
          {para}
        </p>
      ))}

      {Array.isArray(bullets) && bullets.filter(Boolean).length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {bullets.filter(Boolean).map((b, i) => (
            <li key={i} style={{
              ...T.body, color: textColor, fontSize: 16.5, lineHeight: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <span style={{ color: isLightBg ? '#8a6b1a' : GOLD, fontSize: 14, lineHeight: 1.55, flexShrink: 0 }}>✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Bookmark */}
      {onSave && (
        <div style={{ position: 'absolute', top: S.lg, right: S.lg }}>
          <BookmarkIcon filled={saved} color={isLightBg ? '#8a6b1a' : GOLD} onClick={onSave} />
        </div>
      )}
    </div>
  );

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : (variant === 'image-right' ? 'row-reverse' : 'row'),
        overflow: 'hidden',
        borderRadius: 0,
        cursor: onClick ? 'pointer' : 'default',
        minHeight: isMobile ? 0 : cardMin,
      }}
    >
      {imageCol}
      {textCol}
    </div>
  );
}
